"use client";

import { useEffect, useRef } from "react";
import styles from "./PafBand.module.css";
import { PAF_BAND_AMPLITUDE_VW, PAF_BAND_SCRUB, pafBandProgress, pafBandSmoothing, pafBandTarget } from "./pafBandMotion";

type Item = { el: HTMLElement; index: number; current: number };

/*
 * Chaque ligne est écrite trois fois. Le bloc est tourné de -25° et agrandi ×2 :
 * une ligne doit donc couvrir la projection du bandeau (largeur·cos25 +
 * hauteur·sin25) PLUS deux fois l'amplitude du glissement, sinon sa fin entre
 * dans le cadre au scroll et laisse un trou noir. Mesuré sur le bandeau de
 * PafCallout à 1440 px : il faut 2 117 px, or cinq des six lignes n'en
 * faisaient que 1 650 à 1 986. Trois répétitions couvrent largement, à toutes
 * les largeurs d'écran, et le débordement est masqué par `overflow: hidden`.
 */
function repeatToOverflow(line: string): string {
  return `${line} ${line} ${line}`;
}

/** Les lignes du bandeau, animées au scroll (rien si prefers-reduced-motion). */
export function PafBandLines({ lines }: { lines: ReadonlyArray<string> }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const band = root.parentElement ?? root;
    const items: Item[] = Array.from(root.children).map((el, index) => ({ el: el as HTMLElement, index, current: Number.NaN }));
    let frame = 0;
    let running = false;
    let last = 0;

    const tick = (time: number) => {
      const elapsed = Math.min(0.1, Math.max(0, (time - last) / 1000));
      last = time;
      const rect = band.getBoundingClientRect();
      const progress = pafBandProgress(rect.top, rect.height, window.innerHeight);
      const amplitude = (window.innerWidth * PAF_BAND_AMPLITUDE_VW) / 100;
      let settled = true;
      for (const item of items) {
        const target = pafBandTarget(progress, item.index, amplitude);
        const scrub = PAF_BAND_SCRUB[item.index % PAF_BAND_SCRUB.length];
        if (!Number.isFinite(item.current)) item.current = target;
        else item.current += (target - item.current) * pafBandSmoothing(elapsed, scrub);
        if (Math.abs(target - item.current) > 0.2) settled = false;
        item.el.style.transform = `translate3d(${item.current.toFixed(2)}px, 0, 0)`;
      }
      if (settled) {
        running = false;
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    // La boucle ne tourne que tant que les lignes rattrapent leur cible, puis s'endort jusqu'au prochain scroll.
    const wake = () => {
      if (running) return;
      running = true;
      last = performance.now();
      frame = window.requestAnimationFrame(tick);
    };
    wake();
    window.addEventListener("scroll", wake, { passive: true });
    window.addEventListener("resize", wake);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", wake);
      window.removeEventListener("resize", wake);
    };
  }, [lines]);

  return (
    <div ref={ref} className={styles.lines}>
      {lines.map((line, index) => (
        <div key={index} className={styles.line}>
          {repeatToOverflow(line)}
        </div>
      ))}
    </div>
  );
}
